import { useEffect, useMemo, useRef, useState } from 'react';
import Button from '@components/Button';
import Modal from '@components/Modal';
import { showSuccessToast, showErrorToast } from '@lib/toast';
import type { BreathingPreset, BackgroundVideo } from '@lib/api';
import { listBackgroundVideos } from '@lib/backgroundVideos';
import {
  createBreathingPreset,
  deleteBreathingPreset,
  listBreathingPresets,
  type NewBreathingPreset,
  updateBreathingPreset,
} from '@lib/breathingPresets';

type PresetFormState = {
  name: string;
  slug: string;
  inhale: string;
  hold_full: string;
  exhale: string;
  hold_empty: string;
  default_duration: string; // seconds
  default_background: string;
  default_audio: boolean;
  auto_start: boolean;
  is_active: boolean;
  is_public: boolean;
};

const blankForm: PresetFormState = {
  name: '',
  slug: '',
  inhale: '4',
  hold_full: '4',
  exhale: '4',
  hold_empty: '4',
  default_duration: '300',
  default_background: '',
  default_audio: false,
  auto_start: true,
  is_active: true,
  is_public: true,
};

function slugify(value: string): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

const FALLBACK_LOCAL_VIDEOS_KEYS = [
  'none',
  'starfield',
  'fireplace',
  'mountain',
  'forest',
  'aurora',
  'candle',
  'sunset-field',
  'underwater',
  'ocean',
  'ocean-waves',
];

function isValidSlug(value: string): boolean {
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value);
}

function toInt(value: string): number | null {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const i = Math.trunc(n);
  return i;
}

function formatShareUrl(slug: string): string {
  const origin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : '';
  return `${origin}/breathing/${slug}`;
}

async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // ignore
  }

  try {
    const el = document.createElement('textarea');
    el.value = text;
    el.setAttribute('readonly', 'true');
    el.style.position = 'fixed';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(el);
    return ok;
  } catch {
    return false;
  }
}

function ActionsMenu(props: {
  id?: string;
  label?: string;
  items: Array<{
    key: string;
    label: string;
    onClick: () => void;
    className?: string;
    disabled?: boolean;
  }>;
}) {
  const { label = 'Actions', items } = props;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!ref.current) return;
      if (e.target instanceof Node && ref.current.contains(e.target)) return;
      setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [ref]);

  return (
    <div ref={ref as any} className="relative inline-block text-left">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center rounded-full bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-400"
      >
        <span className="sr-only">{label}</span>
        <svg
          className="h-4 w-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
        >
          <circle cx="12" cy="12" r="1" />
          <circle cx="19" cy="12" r="1" />
          <circle cx="5" cy="12" r="1" />
        </svg>
      </button>

      {open ? (
        <div className="absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-2xl border border-gray-100 bg-white shadow-lg">
          <div className="py-2">
            {items.map((it) => (
              <button
                key={it.key}
                type="button"
                onClick={() => {
                  it.onClick();
                  setOpen(false);
                }}
                disabled={it.disabled}
                className={`w-full px-4 py-2 text-left text-sm ${it.className ?? 'text-gray-700'} ${it.disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-gray-50'}`}
              >
                {it.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function AdminBreathingPresets() {
  const [presets, setPresets] = useState<BreathingPreset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [publicFilter, setPublicFilter] = useState<
    'all' | 'public' | 'private'
  >('all');

  const [editing, setEditing] = useState<BreathingPreset | null>(null);
  const [formValues, setFormValues] = useState<PresetFormState>(blankForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState<BreathingPreset | null>(
    null
  );
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [backgroundVideos, setBackgroundVideos] = useState<BackgroundVideo[]>(
    []
  );

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const rows = await listBreathingPresets(true);
        if (!cancelled) setPresets(rows);
      } catch {
        if (!cancelled) setError('Unable to load breathing presets.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const list = await listBackgroundVideos(true);
        if (!cancelled) setBackgroundVideos(list || []);
      } catch {
        if (!cancelled) setBackgroundVideos([]);
      } finally {
        // done
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const t = setTimeout(
      () => setDebouncedSearch(search.trim().toLowerCase()),
      300
    );
    return () => clearTimeout(t);
  }, [search]);

  const preview = useMemo(() => {
    const inhale = toInt(formValues.inhale);
    const holdFull = toInt(formValues.hold_full);
    const exhale = toInt(formValues.exhale);
    const holdEmpty = toInt(formValues.hold_empty);
    const durationSeconds = toInt(formValues.default_duration);

    const ratioParts = [inhale, holdFull, exhale, holdEmpty].map((v) =>
      typeof v === 'number' && v >= 0 ? v : 0
    );
    const ratioString = ratioParts.join('-');
    const cycleSeconds = ratioParts.reduce((a, b) => a + b, 0);
    const minutes =
      typeof durationSeconds === 'number' && durationSeconds > 0
        ? Math.round((durationSeconds / 60) * 10) / 10
        : null;

    return {
      ratioString,
      cycleSeconds,
      minutes,
      shareUrl:
        formValues.slug && isValidSlug(formValues.slug)
          ? formatShareUrl(formValues.slug)
          : null,
    };
  }, [formValues]);

  const keyFromFileName = (fileName: string) => {
    if (!fileName) return '';
    const base =
      String(fileName || '')
        .split('/')
        .pop() || fileName;
    return base.replace(/\.[^.]+$/, '');
  };

  // use the top-level FALLBACK_LOCAL_VIDEOS_KEYS

  const allDropdownOptions = useMemo(() => {
    // DB-derived options (use key from filename when possible, otherwise video id)
    const dbOptions = (backgroundVideos || []).map((v) => {
      const key = keyFromFileName(v.file_name) || v.id;
      return { value: key, label: `${v.name} (${key})` };
    });

    // Local fallback options
    const localOptions = FALLBACK_LOCAL_VIDEOS_KEYS.map((k) => ({
      value: k,
      label: k === 'none' ? 'None' : `${k.replace(/-/g, ' ')} (local)`,
    }));

    // Combine while preserving uniqueness by value
    const map = new Map<string, { value: string; label: string }>();
    for (const opt of localOptions) map.set(opt.value, opt);
    for (const opt of dbOptions) map.set(opt.value, opt);
    return Array.from(map.values());
  }, [backgroundVideos]);

  const dropdownOptionValues = useMemo(() => {
    return new Set(allDropdownOptions.map((o) => o.value));
  }, [allDropdownOptions]);

  const selectedDropdown = (() => {
    const raw = String(formValues.default_background || '').trim();
    const normalized = raw === '' ? 'none' : raw;
    return dropdownOptionValues.has(normalized) ? normalized : 'custom';
  })();

  // (removed redundant duplicate block)

  const openCreate = () => {
    setEditing(null);
    setFormValues({ ...blankForm });
    setFormError(null);
    setShowForm(true);
  };

  const openEdit = (preset: BreathingPreset) => {
    setEditing(preset);
    setFormValues({
      name: preset.name,
      slug: preset.slug,
      inhale: String(preset.inhale),
      hold_full: String(preset.hold_full),
      exhale: String(preset.exhale),
      hold_empty: String(preset.hold_empty),
      default_duration: String(preset.default_duration),
      default_background: preset.default_background ?? '',
      default_audio: Boolean(preset.default_audio),
      auto_start: Boolean(preset.auto_start),
      is_active: Boolean(preset.is_active),
      is_public: Boolean(preset.is_public),
    });
    setFormError(null);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setSaving(false);
  };

  const updateField = <K extends keyof PresetFormState>(
    key: K,
    value: PresetFormState[K]
  ) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  };

  const submit = async () => {
    setFormError(null);

    const name = formValues.name.trim();
    const slug = formValues.slug.trim().toLowerCase();
    if (!name) {
      setFormError('Name is required.');
      return;
    }

    if (!slug) {
      setFormError('Slug is required.');
      return;
    }

    if (!isValidSlug(slug)) {
      setFormError(
        'Slug must be URL-safe (lowercase letters, numbers, hyphens).'
      );
      return;
    }

    const inhale = toInt(formValues.inhale);
    const holdFull = toInt(formValues.hold_full);
    const exhale = toInt(formValues.exhale);
    const holdEmpty = toInt(formValues.hold_empty);
    const defaultDuration = toInt(formValues.default_duration);

    const ints = [inhale, holdFull, exhale, holdEmpty, defaultDuration];
    if (ints.some((v) => typeof v !== 'number' || !Number.isFinite(v))) {
      setFormError('All timing fields must be valid numbers.');
      return;
    }

    if ((defaultDuration as number) <= 0) {
      setFormError('Default duration must be greater than 0 seconds.');
      return;
    }

    if (
      [inhale, holdFull, exhale, holdEmpty].some(
        (v) => (v as number) < 0 || (v as number) > 60
      )
    ) {
      setFormError('Phase seconds must be between 0 and 60.');
      return;
    }

    const payload: NewBreathingPreset = {
      name,
      slug,
      inhale: inhale as number,
      hold_full: holdFull as number,
      exhale: exhale as number,
      hold_empty: holdEmpty as number,
      default_duration: defaultDuration as number,
      default_background: formValues.default_background.trim() || null,
      default_audio: Boolean(formValues.default_audio),
      auto_start: Boolean(formValues.auto_start),
      is_active: Boolean(formValues.is_active),
      is_public: Boolean(formValues.is_public),
    };

    setSaving(true);

    try {
      if (editing) {
        const updated = await updateBreathingPreset(editing.id, payload);
        setPresets((prev) =>
          prev.map((p) => (p.id === updated.id ? updated : p))
        );
      } else {
        const created = await createBreathingPreset(payload);
        setPresets((prev) => [...prev, created]);
      }
      setShowForm(false);
      setEditing(null);
      setFormValues(blankForm);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Unable to save preset.';
      setFormError(message || 'Unable to save preset.');
    } finally {
      setSaving(false);
    }
  };

  const sortedPresets = useMemo(() => {
    return [...presets].sort((a, b) => a.name.localeCompare(b.name));
  }, [presets]);

  const stats = useMemo(() => {
    const total = presets.length;
    const active = presets.filter((p) => p.is_active).length;
    const pub = presets.filter((p) => p.is_public).length;
    return { total, active, pub };
  }, [presets]);

  const filteredPresets = useMemo(() => {
    const s = debouncedSearch;
    return sortedPresets.filter((p) => {
      if (publicFilter === 'public' && !p.is_public) return false;
      if (publicFilter === 'private' && p.is_public) return false;
      if (!s) return true;
      const hay =
        `${p.name} ${p.slug} ${p.inhale}-${p.hold_full}-${p.exhale}-${p.hold_empty} ${p.default_background || ''}`.toLowerCase();
      return hay.includes(s);
    });
  }, [sortedPresets, debouncedSearch, publicFilter]);

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-blue-100 via-teal-50 to-cyan-100">
      <div
        className="absolute inset-0 animate-pulse bg-gradient-to-br from-teal-200/30 via-blue-200/20 to-cyan-200/30"
        style={{ animationDuration: '8s' }}
        aria-hidden="true"
      />

      <div className="relative mx-auto max-w-5xl px-4 py-12">
        <div className="mb-6 text-center">
          <h1 className="gradient-text mb-2 text-4xl font-bold">
            Breathing Presets (Admin)
          </h1>
          <p className="text-lg text-gray-600">
            Manage shareable presets used by /breathing/:presetSlug.
          </p>
        </div>

        <div className="animate-fade-in rounded-3xl bg-white/70 p-8 shadow-xl backdrop-blur">
          <div className="mb-4 flex items-center justify-between gap-4">
            <div className="flex flex-1 items-center gap-4">
              <div className="w-full max-w-2xl">
                <label htmlFor="admin-preset-search" className="sr-only">
                  Search presets
                </label>
                <div className="relative rounded-lg border border-gray-200 bg-white/60 px-3 py-2 shadow-sm">
                  <svg
                    className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      d="M21 21l-4.35-4.35"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle
                      cx="11"
                      cy="11"
                      r="6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <input
                    id="admin-preset-search"
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search presets by name, slug, ratio or background"
                    className="w-full border-0 bg-transparent pl-9 text-sm text-gray-700 placeholder-gray-400 focus:outline-none"
                  />
                  {search && (
                    <button
                      onClick={() => setSearch('')}
                      type="button"
                      aria-label="Clear search"
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"
                    >
                      clear
                    </button>
                  )}
                </div>
              </div>
              <div className="w-full max-w-xs sm:w-auto">
                <label htmlFor="filter-public" className="sr-only">
                  Filter by visibility
                </label>
                <select
                  id="filter-public"
                  value={publicFilter}
                  onChange={(e) => setPublicFilter(e.target.value as any)}
                  className="mt-2 w-full rounded-2xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:mt-0"
                >
                  <option value="all">All</option>
                  <option value="public">Public</option>
                  <option value="private">Private</option>
                </select>
              </div>
            </div>
            <div className="flex shrink-0">
              <Button
                type="button"
                onClick={openCreate}
                className="item-selected inline-flex items-center rounded-full bg-teal-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:ring-offset-2"
              >
                + New Preset
              </Button>
            </div>
          </div>

          {error && (
            <div className="mt-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="mt-6">
            {loading ? (
              <div className="space-y-4">
                {[0, 1, 2].map((index) => (
                  <div
                    key={index}
                    className="h-28 animate-pulse rounded-2xl bg-white/60 backdrop-blur"
                  />
                ))}
              </div>
            ) : filteredPresets.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-teal-200 bg-white/60 px-6 py-12 text-center text-gray-600">
                No breathing presets yet. Create the first one to get started.
              </div>
            ) : (
              <>
                <div className="mb-6 flex flex-wrap justify-center gap-6 text-sm text-gray-600">
                  <div>
                    <span className="font-semibold text-gray-900">
                      {stats.total}
                    </span>{' '}
                    total
                  </div>
                  <div>
                    <span className="font-semibold text-gray-900">
                      {stats.active}
                    </span>{' '}
                    active
                  </div>
                  <div>
                    <span className="font-semibold text-gray-900">
                      {stats.pub}
                    </span>{' '}
                    public
                  </div>
                </div>

                <div className="space-y-4 lg:hidden">
                  {filteredPresets.map((preset) => {
                    const shareUrl = formatShareUrl(preset.slug);
                    const statusPill = preset.is_active
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'bg-rose-100 text-rose-800';
                    const publicPill = preset.is_public
                      ? 'bg-sky-100 text-sky-800'
                      : 'bg-gray-100 text-gray-700';

                    return (
                      <div
                        key={preset.id}
                        className="rounded-2xl border border-gray-100 bg-white/90 p-5 shadow-sm"
                      >
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                          <div className="min-w-0">
                            <h2 className="text-lg font-semibold text-gray-900">
                              {preset.name}
                            </h2>
                            <div className="mt-2 flex flex-wrap gap-2">
                              <span
                                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${statusPill}`}
                              >
                                {preset.is_active ? 'Active' : 'Disabled'}
                              </span>
                              <span
                                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${publicPill}`}
                              >
                                {preset.is_public ? 'Public' : 'Private'}
                              </span>
                              {preset.auto_start && (
                                <span className="inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-800">
                                  Auto-start
                                </span>
                              )}
                            </div>

                            <div className="mt-3 grid grid-cols-1 gap-2 text-sm text-gray-700 sm:grid-cols-2">
                              <div>
                                <span className="font-semibold text-gray-800">
                                  Slug:
                                </span>{' '}
                                <span className="font-mono text-gray-700">
                                  {preset.slug}
                                </span>
                              </div>
                              <div>
                                <span className="font-semibold text-gray-800">
                                  Ratio:
                                </span>{' '}
                                <span className="font-mono text-gray-700">
                                  {preset.inhale}-{preset.hold_full}-
                                  {preset.exhale}-{preset.hold_empty}
                                </span>
                              </div>
                              <div>
                                <span className="font-semibold text-gray-800">
                                  Duration:
                                </span>{' '}
                                {preset.default_duration}s
                              </div>
                              <div>
                                <span className="font-semibold text-gray-800">
                                  Background:
                                </span>{' '}
                                {preset.default_background || '—'}
                              </div>
                            </div>

                            <div className="mt-3 flex flex-wrap items-center gap-2">
                              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Share URL
                              </span>
                              <a
                                href={shareUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="truncate rounded-md bg-gray-50 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100"
                              >
                                {shareUrl}
                              </a>
                              <button
                                type="button"
                                className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-gray-700 shadow-sm ring-1 ring-gray-200 transition hover:bg-gray-50"
                                onClick={async () => {
                                  try {
                                    const ok = await copyText(shareUrl);
                                    if (ok)
                                      showSuccessToast(
                                        'Copied URL to clipboard'
                                      );
                                    else
                                      showErrorToast(
                                        'Unable to copy to clipboard'
                                      );
                                  } catch (err) {
                                    showErrorToast(
                                      'Unable to copy to clipboard'
                                    );
                                  }
                                }}
                              >
                                Copy URL
                              </button>
                            </div>
                          </div>

                          <div className="flex shrink-0 flex-row gap-2 sm:flex-col sm:items-end">
                            <ActionsMenu
                              items={[
                                {
                                  key: 'edit',
                                  label: 'Edit',
                                  onClick: () => openEdit(preset),
                                  className: '!text-indigo-700',
                                },
                                {
                                  key: 'copy',
                                  label: 'Copy link',
                                  onClick: async () => {
                                    try {
                                      const u = formatShareUrl(preset.slug);
                                      const ok = await copyText(u);
                                      if (ok)
                                        showSuccessToast(
                                          'Copied URL to clipboard'
                                        );
                                      else
                                        showErrorToast(
                                          'Unable to copy to clipboard'
                                        );
                                    } catch (err) {
                                      showErrorToast(
                                        'Unable to copy to clipboard'
                                      );
                                    }
                                  },
                                  className: '!text-gray-700',
                                },
                                {
                                  key: 'delete',
                                  label: 'Delete',
                                  onClick: () => setConfirmDelete(preset),
                                  className: '!text-rose-700',
                                },
                              ]}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="hidden lg:block">
                  <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white">
                    <table className="min-w-full text-left text-sm text-gray-800">
                      <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        <tr>
                          <th className="px-4 py-3">Name</th>
                          <th className="px-4 py-3">Slug</th>
                          <th className="px-4 py-3">Ratio</th>
                          <th className="px-4 py-3">Duration</th>
                          <th className="px-4 py-3">Background</th>
                          <th className="px-4 py-3">Public</th>
                          <th className="px-4 py-3">Auto-start</th>
                          <th className="px-4 py-3">Status</th>
                          <th className="px-4 py-3">Updated</th>
                          <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPresets.map((preset) => (
                          <tr
                            key={preset.id}
                            className="border-t border-gray-100 hover:bg-indigo-50"
                          >
                            <td className="px-4 py-4 align-top text-gray-900">
                              {preset.name}
                            </td>
                            <td className="px-4 py-4 align-top text-gray-900">
                              {preset.slug}
                            </td>
                            <td className="px-4 py-4 align-top text-gray-900">
                              {preset.inhale}-{preset.hold_full}-{preset.exhale}
                              -{preset.hold_empty}
                            </td>
                            <td className="px-4 py-4 align-top text-gray-900">
                              {preset.default_duration}s
                            </td>
                            <td className="px-4 py-4 align-top text-gray-900">
                              {preset.default_background || '—'}
                            </td>
                            <td className="px-4 py-4 align-top text-gray-900">
                              {preset.is_public ? 'Yes' : 'No'}
                            </td>
                            <td className="px-4 py-4 align-top text-gray-900">
                              {preset.auto_start ? 'Yes' : 'No'}
                            </td>
                            <td className="px-4 py-4 align-top text-gray-900">
                              {preset.is_active ? 'Active' : 'Disabled'}
                            </td>
                            <td className="px-4 py-4 align-top text-gray-900">
                              {new Date(preset.updated_at).toLocaleString()}
                            </td>
                            <td className="px-4 py-4 text-right">
                              <div className="flex flex-wrap justify-end gap-3">
                                <ActionsMenu
                                  items={[
                                    {
                                      key: 'edit',
                                      label: 'Edit',
                                      onClick: () => openEdit(preset),
                                      className: '!text-indigo-700',
                                    },
                                    {
                                      key: 'copy',
                                      label: 'Copy link',
                                      onClick: async () => {
                                        try {
                                          const u = formatShareUrl(preset.slug);
                                          const ok = await copyText(u);
                                          if (ok)
                                            showSuccessToast(
                                              'Copied URL to clipboard'
                                            );
                                          else
                                            showErrorToast(
                                              'Unable to copy to clipboard'
                                            );
                                        } catch (err) {
                                          showErrorToast(
                                            'Unable to copy to clipboard'
                                          );
                                        }
                                      },
                                      className: '!text-gray-700',
                                    },
                                    {
                                      key: 'delete',
                                      label: 'Delete',
                                      onClick: () => setConfirmDelete(preset),
                                      className: '!text-rose-700',
                                    },
                                  ]}
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {showForm && (
        <Modal
          title={editing ? `Edit preset — ${editing.name}` : 'New preset'}
          onClose={closeForm}
          className="max-w-3xl"
        >
          <div className="flex max-h-[calc(100vh-200px)] w-full flex-col overflow-hidden">
            <div className="space-y-5 overflow-auto pr-2">
              {formError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {formError}
                </div>
              )}

              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-800">
                    Name
                  </label>
                  <input
                    className="mt-1 w-full rounded-xl border border-gray-200 bg-white/80 px-4 py-2 text-gray-900 outline-none ring-0 focus:border-teal-300"
                    value={formValues.name}
                    onChange={(e) => {
                      const nextName = e.target.value;
                      updateField('name', nextName);
                      if (!editing && !formValues.slug.trim()) {
                        updateField('slug', slugify(nextName));
                      }
                    }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800">
                    Slug
                  </label>
                  <div className="mt-1 flex gap-2">
                    <input
                      className="w-full rounded-xl border border-gray-200 bg-white/80 px-4 py-2 font-mono text-gray-900 outline-none focus:border-teal-300"
                      value={formValues.slug}
                      onChange={(e) => updateField('slug', e.target.value)}
                    />
                    <button
                      type="button"
                      className="shrink-0 rounded-xl bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-200"
                      onClick={() =>
                        updateField('slug', slugify(formValues.name))
                      }
                    >
                      Generate
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Used in URL: /breathing/{formValues.slug || 'your-slug'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                {(
                  [
                    ['inhale', 'Inhale'],
                    ['hold_full', 'Hold (full)'],
                    ['exhale', 'Exhale'],
                    ['hold_empty', 'Hold (empty)'],
                  ] as const
                ).map(([key, label]) => (
                  <div key={key}>
                    <label className="block text-sm font-semibold text-gray-800">
                      {label}
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={60}
                      className="mt-1 w-full rounded-xl border border-gray-200 bg-white/80 px-4 py-2 text-gray-900 outline-none focus:border-teal-300"
                      value={formValues[key]}
                      onChange={(e) => updateField(key, e.target.value)}
                    />
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-semibold text-gray-800">
                    Default duration (seconds)
                  </label>
                  <input
                    type="number"
                    min={10}
                    className="mt-1 w-full rounded-xl border border-gray-200 bg-white/80 px-4 py-2 text-gray-900 outline-none focus:border-teal-300"
                    value={formValues.default_duration}
                    onChange={(e) =>
                      updateField('default_duration', e.target.value)
                    }
                  />
                  {preview.minutes !== null && (
                    <p className="mt-1 text-xs text-gray-500">
                      About {preview.minutes} minutes.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-800">
                    Default background (key)
                  </label>
                  <div className="mt-1 flex w-full gap-2">
                    <select
                      className="w-full rounded-xl border border-gray-200 bg-white/80 px-4 py-2 text-gray-900 outline-none focus:border-teal-300"
                      value={selectedDropdown}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === 'custom') {
                          // keep current value; allow manual input
                          updateField(
                            'default_background',
                            formValues.default_background || ''
                          );
                        } else {
                          updateField('default_background', v);
                        }
                      }}
                      aria-label="Default background key"
                    >
                      <option value="none">None</option>
                      <optgroup label="Local">
                        {FALLBACK_LOCAL_VIDEOS_KEYS.map((k) => (
                          <option key={k} value={k}>
                            {k === 'none' ? 'None' : k.replace(/-/g, ' ')}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="Background videos">
                        {backgroundVideos.map((v) => {
                          const key = keyFromFileName(v.file_name) || v.id;
                          return (
                            <option key={v.id} value={key}>
                              {v.name} — {key}
                            </option>
                          );
                        })}
                      </optgroup>
                      <option value="custom">Custom value…</option>
                    </select>

                    {selectedDropdown === 'custom' && (
                      <input
                        className="w-full rounded-xl border border-gray-200 bg-white/80 px-4 py-2 text-gray-900 outline-none focus:border-teal-300"
                        value={formValues.default_background}
                        onChange={(e) =>
                          updateField('default_background', e.target.value)
                        }
                        placeholder="Custom key (e.g. my-custom-id)"
                      />
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Uses the same keys as the breathing page background
                    selection.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {(
                  [
                    ['default_audio', 'Default audio'],
                    ['auto_start', 'Auto-start'],
                    ['is_active', 'Enabled'],
                  ] as const
                ).map(([key, label]) => (
                  <label
                    key={key}
                    className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white/70 px-4 py-3 text-sm font-semibold text-gray-800"
                  >
                    <input
                      type="checkbox"
                      checked={Boolean(formValues[key])}
                      onChange={(e) =>
                        updateField(key, e.target.checked as any)
                      }
                    />
                    {label}
                  </label>
                ))}
                <label className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white/70 px-4 py-3 text-sm font-semibold text-gray-800">
                  <input
                    type="checkbox"
                    checked={Boolean(formValues.is_public)}
                    onChange={(e) => updateField('is_public', e.target.checked)}
                  />
                  Public
                </label>
              </div>

              <div className="rounded-2xl border border-gray-200 bg-white/70 p-5">
                <h3 className="text-sm font-semibold text-gray-900">Preview</h3>
                <div className="mt-3 grid grid-cols-1 gap-2 text-sm text-gray-700 sm:grid-cols-2">
                  <div>
                    <span className="font-semibold text-gray-800">Ratio:</span>{' '}
                    <span className="font-mono">{preview.ratioString}</span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-800">Cycle:</span>{' '}
                    {preview.cycleSeconds}s
                  </div>
                  <div>
                    <span className="font-semibold text-gray-800">Audio:</span>{' '}
                    {formValues.default_audio ? 'On' : 'Off'}
                  </div>
                  <div>
                    <span className="font-semibold text-gray-800">
                      Auto-start:
                    </span>{' '}
                    {formValues.auto_start ? 'Yes' : 'No'}
                  </div>
                </div>

                {preview.shareUrl && (
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Share URL
                    </span>
                    <a
                      href={preview.shareUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="truncate rounded-md bg-gray-50 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100"
                    >
                      {preview.shareUrl}
                    </a>
                    <button
                      type="button"
                      className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-gray-700 shadow-sm ring-1 ring-gray-200 transition hover:bg-gray-50"
                      onClick={async () => {
                        try {
                          const url = preview.shareUrl!;
                          const ok = await copyText(url);
                          if (ok) showSuccessToast('Copied URL to clipboard');
                          else showErrorToast('Unable to copy to clipboard');
                        } catch (err) {
                          showErrorToast('Unable to copy to clipboard');
                        }
                      }}
                    >
                      Copy URL
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="sticky bottom-0 flex justify-end gap-3 border-t border-gray-100 bg-white/95 py-3">
              <Button type="button" onClick={closeForm}>
                Cancel
              </Button>
              <Button
                type="button"
                disabled={saving}
                className="!bg-indigo-600 !text-white hover:!bg-indigo-700"
                onClick={submit}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {confirmDelete && (
        <Modal
          title={`Delete preset — ${confirmDelete.name}`}
          onClose={() => setConfirmDelete(null)}
        >
          <p className="text-sm text-gray-700">
            This will permanently delete the preset.
          </p>

          {deleteError && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {deleteError}
            </div>
          )}

          <div className="mt-6 flex justify-end gap-3">
            <Button type="button" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={deleting}
              className="!bg-rose-600 !text-white hover:!bg-rose-700"
              onClick={async () => {
                if (!confirmDelete) return;
                setDeleting(true);
                setDeleteError(null);
                try {
                  await deleteBreathingPreset(confirmDelete.id);
                  setPresets((prev) =>
                    prev.filter((p) => p.id !== confirmDelete.id)
                  );
                  setConfirmDelete(null);
                } catch (err) {
                  const message =
                    err instanceof Error
                      ? err.message
                      : 'Failed to delete preset.';
                  setDeleteError(message);
                } finally {
                  setDeleting(false);
                }
              }}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}
